import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL, API_URL_Image } from '../../api/api';
import {
    Search,
    Filter,
    Download,
    Eye,
    Edit,
    Trash2,
    MoreVertical,
    UserPlus,
    RefreshCw,
    Users,
    Mail,
    UserCircle,
    CheckCircle,
    XCircle,
    Calendar,
    Shield,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    TrendingUp,
    TrendingDown
} from 'lucide-react';

const API_USERS = `${API_URL}/user`;
const token = localStorage.getItem('token');

const UsersListLayer = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const navigate = useNavigate();

    // Search and Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Sort states
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    // Stats states
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        admin: 0,
        manager: 0
    });

    // Fetch users
    const fetchUsers = useCallback(async () => {
        setRefreshing(true);
        try {
            const response = await axios.get(API_USERS, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(response.data || []);
            calculateStats(response.data || []);
        } catch (error) {
            console.error('Error fetching users:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch users',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Calculate statistics
    const calculateStats = (data) => {
        const stats = {
            total: data.length,
            active: data.filter(u => u.status?.toLowerCase() === 'active').length,
            inactive: data.filter(u => u.status?.toLowerCase() === 'inactive').length,
            admin: data.filter(u => u.role?.toLowerCase() === 'admin').length,
            manager: data.filter(u => u.role?.toLowerCase() === 'manager').length
        };
        setStats(stats);
    };

    // Filter users
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesSearch =
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.username?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesRole =
                selectedRole === 'all' ||
                user.role?.toLowerCase() === selectedRole.toLowerCase();

            const matchesStatus =
                selectedStatus === 'all' ||
                user.status?.toLowerCase() === selectedStatus.toLowerCase();

            return matchesSearch && matchesRole && matchesStatus;
        });
    }, [users, searchTerm, selectedRole, selectedStatus]);

    // Sort users
    const sortedUsers = useMemo(() => {
        const sortableItems = [...filteredUsers];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                // Handle nested properties
                if (sortConfig.key.includes('.')) {
                    const keys = sortConfig.key.split('.');
                    const aVal = keys.reduce((obj, key) => obj?.[key], a);
                    const bVal = keys.reduce((obj, key) => obj?.[key], b);

                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                }

                // Handle dates
                if (sortConfig.key.includes('date') || sortConfig.key === 'created_at') {
                    const aDate = new Date(aValue);
                    const bDate = new Date(bValue);
                    return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
                }

                // Handle strings
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortConfig.direction === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                return 0;
            });
        }
        return sortableItems;
    }, [filteredUsers, sortConfig]);

    // Pagination
    const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentUsers = sortedUsers.slice(startIndex, endIndex);

    // Handle sort
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Handle delete user
    const handleDeleteUser = (id, name) => {
        Swal.fire({
            title: 'Delete User',
            html: `Are you sure you want to delete <strong>${name}</strong>?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${API_USERS}/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'User has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    fetchUsers();
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to delete user',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }
        });
    };

    // Handle status toggle
    const handleStatusToggle = async (userId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

        try {
            await axios.put(`${API_USERS}/${userId}/status`, {
                status: newStatus
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: `User status updated to ${newStatus}`,
                timer: 1500,
                showConfirmButton: false
            });

            fetchUsers();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update status',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };

    // Handle export
    const handleExport = () => {
        const exportData = sortedUsers.map(user => ({
            'User ID': user.id,
            'Name': user.name || '',
            'Email': user.email || '',
            'Username': user.username || '',
            'Role': user.role || '',
            'Status': user.status || '',
            'Join Date': new Date(user.created_at).toLocaleDateString(),
            'Last Login': user.last_login || 'Never',
            'Phone': user.phone || '',
            'Address': user.address || ''
        }));

        // Create CSV content
        const headers = Object.keys(exportData[0] || {}).join(',');
        const rows = exportData.map(item =>
            Object.values(item).map(value =>
                typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
            ).join(',')
        );
        const csvContent = [headers, ...rows].join('\n');

        // Create Excel (CSV) file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        link.setAttribute('download', `users-export-${timestamp}.csv`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        Swal.fire({
            icon: 'success',
            title: 'Export Complete!',
            text: `Exported ${exportData.length} users to CSV file`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    // Loading skeleton
    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={innerContainerStyle}>
                    <div style={skeletonContainer}>
                        <div style={skeletonTitle}></div>
                        <div style={statsGridStyle}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={skeletonCard}></div>
                            ))}
                        </div>
                        <div style={skeletonTable}>
                            <div style={skeletonHeader}></div>
                            <div style={skeletonRows}>
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} style={skeletonRow}></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={innerContainerStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <div>
                        <h4 style={titleStyle}>User Management</h4>
                        <p style={subtitleStyle}>Manage and monitor all system users</p>
                    </div>

                    <div style={headerActionsStyle}>
                        <button
                            onClick={() => fetchUsers()}
                            disabled={refreshing}
                            style={refreshing ? refreshButtonDisabledStyle : refreshButtonStyle}
                            className="refresh-button"
                        >
                            <RefreshCw size={16} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <button
                            onClick={handleExport}
                            style={exportButtonStyle}
                            className="export-button"
                        >
                            <Download size={16} />
                            Export
                        </button>

                        <Link
                            to="/add-user"
                            style={addButtonStyle}
                            className="add-button"
                        >
                            <UserPlus size={16} />
                            Add New User
                        </Link>
                    </div>
                </div>

                {/* Stats Cards */}
                <div style={statsGridStyle}>
                    <div style={statCardBlueStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Total Users</p>
                                <p style={statValueBlueStyle}>{stats.total}</p>
                            </div>
                            <div style={statIconBlueStyle}>
                                <Users size={24} />
                            </div>
                        </div>
                    </div>

                    <div style={statCardGreenStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Active Users</p>
                                <p style={statValueGreenStyle}>{stats.active}</p>
                            </div>
                            <div style={statIconGreenStyle}>
                                <CheckCircle size={24} />
                            </div>
                        </div>
                    </div>

                    <div style={statCardPurpleStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Admin Users</p>
                                <p style={statValuePurpleStyle}>{stats.admin}</p>
                            </div>
                            <div style={statIconPurpleStyle}>
                                <Shield size={24} />
                            </div>
                        </div>
                    </div>

                    <div style={statCardAmberStyle} className="stat-card">
                        <div style={statCardContentStyle}>
                            <div>
                                <p style={statLabelStyle}>Inactive Users</p>
                                <p style={statValueAmberStyle}>{stats.inactive}</p>
                            </div>
                            <div style={statIconAmberStyle}>
                                <TrendingDown size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <div style={filtersCardStyle}>
                    <div style={filtersCardHeaderStyle}>
                        <h5 style={filtersTitleStyle}>Search & Filter</h5>
                    </div>

                    <div style={filtersContentStyle}>
                        <div style={filtersGridStyle}>
                            {/* Search */}
                            <div>
                                <label style={filterLabelStyle}>Search Users</label>
                                <div style={searchContainerStyle}>
                                    <Search style={searchIconStyle} size={18} />
                                    <input
                                        type="text"
                                        placeholder="Search by name, email, or username..."
                                        style={searchInputStyle}
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="search-input"
                                    />
                                </div>
                            </div>

                            {/* Role Filter */}
                            <div>
                                <label style={filterLabelStyle}>Role</label>
                                <select
                                    style={selectInputStyle}
                                    value={selectedRole}
                                    onChange={(e) => {
                                        setSelectedRole(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="select-input"
                                >
                                    <option value="all">All Roles</option>
                                    <option value="admin">Admin</option>
                                    <option value="manager">Manager</option>
                                    <option value="staff">Staff</option>
                                    <option value="user">User</option>
                                </select>
                            </div>

                            {/* Status Filter */}
                            <div>
                                <label style={filterLabelStyle}>Status</label>
                                <select
                                    style={selectInputStyle}
                                    value={selectedStatus}
                                    onChange={(e) => {
                                        setSelectedStatus(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="select-input"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        {/* Active filters display */}
                        {(searchTerm || selectedRole !== 'all' || selectedStatus !== 'all') && (
                            <div style={activeFiltersStyle}>
                                <span style={activeFiltersLabelStyle}>Active filters:</span>
                                {searchTerm && (
                                    <span style={filterTagBlueStyle} className="filter-tag">
                                        Search: {searchTerm}
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            style={filterTagCloseStyle}
                                            className="filter-tag-close"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                )}
                                {selectedRole !== 'all' && (
                                    <span style={filterTagGreenStyle} className="filter-tag">
                                        Role: {selectedRole}
                                        <button
                                            onClick={() => setSelectedRole('all')}
                                            style={filterTagCloseStyle}
                                            className="filter-tag-close"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                )}
                                {selectedStatus !== 'all' && (
                                    <span style={filterTagPurpleStyle} className="filter-tag">
                                        Status: {selectedStatus}
                                        <button
                                            onClick={() => setSelectedStatus('all')}
                                            style={filterTagCloseStyle}
                                            className="filter-tag-close"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Table Card */}
                <div style={tableCardStyle}>
                    <div style={tableHeaderStyle}>
                        <div>
                            <h5 style={tableTitleStyle}>Users List</h5>
                            <p style={tableSubtitleStyle}>
                                Showing {currentUsers.length} of {sortedUsers.length} users
                            </p>
                        </div>

                        <div style={tableControlsStyle}>
                            <div style={itemsPerPageStyle}>
                                <span style={itemsPerPageLabelStyle}>Show:</span>
                                <select
                                    style={itemsPerPageSelectStyle}
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                </select>
                                <span style={itemsPerPageLabelStyle}>per page</span>
                            </div>
                        </div>
                    </div>

                    <div style={tableWrapperStyle} className="table-wrapper">
                        <table style={tableStyle}>
                            <thead style={tableHeadStyle}>
                                <tr>
                                    {[
                                        { key: 'name', label: 'User' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'role', label: 'Role' },
                                        { key: 'username', label: 'Username' },
                                        { key: 'created_at', label: 'Join Date' },
                                        { key: 'status', label: 'Status' },
                                        { key: 'actions', label: 'Actions' }
                                    ].map(({ key, label }) => (
                                        <th
                                            key={key}
                                            style={key !== 'actions' ? sortableHeaderCellStyle : headerCellStyle}
                                            onClick={() => key !== 'actions' && handleSort(key)}
                                            className={key !== 'actions' ? 'sortable-header-cell' : ''}
                                        >
                                            <div style={headerCellContentStyle}>
                                                {label}
                                                {key !== 'actions' && sortConfig.key === key && (
                                                    <span style={sortIndicatorStyle}>
                                                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody style={tableBodyStyle}>
                                {currentUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={emptyStateStyle}>
                                            <div style={emptyStateContentStyle}>
                                                <Users style={emptyStateIconStyle} size={48} />
                                                <h3 style={emptyStateTitleStyle}>No users found</h3>
                                                <p style={emptyStateTextStyle}>
                                                    {searchTerm || selectedRole !== 'all' || selectedStatus !== 'all'
                                                        ? 'Try adjusting your filters to find what you\'re looking for.'
                                                        : 'Get started by adding your first user.'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentUsers.map((user, index) => (
                                        <tr key={user.id} style={tableRowStyle} className="table-row">
                                            <td style={tableCellStyle}>
                                                <div style={userCellStyle}>
                                                    <div style={avatarContainerStyle}>
                                                        {user.image ? (
                                                            <img
                                                                src={`${API_URL_Image}/${user.image}`}
                                                                alt={user.name}
                                                                style={avatarStyle}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = '/assets/images/user-placeholder.png';
                                                                }}
                                                            />
                                                        ) : (
                                                            <div style={avatarPlaceholderStyle}>
                                                                <UserCircle size={20} />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div style={userNameStyle}>{user.name}</div>
                                                        <div style={userIdStyle}>ID: {user.id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={emailCellStyle}>
                                                    <Mail size={14} style={{ marginRight: '8px' }} />
                                                    <a href={`mailto:${user.email}`} style={emailLinkStyle}>
                                                        {user.email}
                                                    </a>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <span style={roleBadgeStyle}>
                                                    {user.role === 'admin' ? (
                                                        <>
                                                            <Shield size={14} style={{ marginRight: '4px' }} />
                                                            Admin
                                                        </>
                                                    ) : user.role === 'manager' ? (
                                                        'Manager'
                                                    ) : user.role === 'staff' ? (
                                                        'Staff'
                                                    ) : (
                                                        'User'
                                                    )}
                                                </span>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={usernameStyle}>
                                                    <UserCircle size={14} style={{ marginRight: '8px' }} />
                                                    {user.username}
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={dateCellStyle}>
                                                    <Calendar size={14} style={{ marginRight: '8px' }} />
                                                    <div>
                                                        <div style={dateValueStyle}>
                                                            {new Date(user.created_at).toLocaleDateString()}
                                                        </div>
                                                        <div style={dateAgoStyle}>
                                                            {Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))} days ago
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <button
                                                    onClick={() => handleStatusToggle(user.id, user.status)}
                                                    style={
                                                        user.status?.toLowerCase() === 'active'
                                                            ? statusButtonActiveStyle
                                                            : statusButtonInactiveStyle
                                                    }
                                                    className="status-button"
                                                >
                                                    {user.status?.toLowerCase() === 'active' ? (
                                                        <>
                                                            <CheckCircle size={14} />
                                                            Active
                                                        </>
                                                    ) : (
                                                        <>
                                                            <XCircle size={14} />
                                                            Inactive
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <div style={actionButtonsStyle}>
                                                    {/* <Link
                                                        to={`/user/view/${user.id}`}
                                                        style={actionButtonViewStyle}
                                                        title="View Details"
                                                        className="action-button-view"
                                                    >
                                                        <Eye size={18} />
                                                    </Link> */}


                                                    <Link
                                                        to={`/add-user/${user.id}`}
                                                        style={actionButtonEditStyle}
                                                        title="Edit"
                                                        className="action-button-edit"
                                                    >
                                                        <Edit size={18} />
                                                    </Link>

                                                    <button
                                                        onClick={() => handleDeleteUser(user.id, user.name)}
                                                        style={actionButtonDeleteStyle}
                                                        title="Delete"
                                                        className="action-button-delete"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>

                                                    {/* <button
                                                        style={actionButtonMoreStyle}
                                                        title="More options"
                                                        className="action-button-more"
                                                    >
                                                        <MoreVertical size={18} />
                                                    </button> */}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {sortedUsers.length > 0 && (
                        <div style={paginationContainerStyle} className="pagination-container">
                            <div style={paginationInfoStyle}>
                                Showing <span style={paginationInfoBoldStyle}>{startIndex + 1}</span> to{' '}
                                <span style={paginationInfoBoldStyle}>{Math.min(endIndex, sortedUsers.length)}</span> of{' '}
                                <span style={paginationInfoBoldStyle}>{sortedUsers.length}</span> results
                            </div>

                            <div style={paginationControlsStyle} className="pagination-controls">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    style={currentPage === 1 ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronsLeft size={16} />
                                </button>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    style={currentPage === 1 ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronLeft size={16} />
                                </button>

                                <div style={paginationNumbersStyle}>
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                style={
                                                    currentPage === pageNum
                                                        ? paginationNumberActiveStyle
                                                        : paginationNumberStyle
                                                }
                                                className="pagination-number"
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    {totalPages > 5 && currentPage < totalPages - 2 && (
                                        <>
                                            <span style={paginationEllipsisStyle}>...</span>
                                            <button
                                                onClick={() => setCurrentPage(totalPages)}
                                                style={
                                                    currentPage === totalPages
                                                        ? paginationNumberActiveStyle
                                                        : paginationNumberStyle
                                                }
                                                className="pagination-number"
                                            >
                                                {totalPages}
                                            </button>
                                        </>
                                    )}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    style={currentPage === totalPages ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronRight size={16} />
                                </button>

                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    style={currentPage === totalPages ? paginationButtonDisabledStyle : paginationButtonStyle}
                                    className="pagination-button"
                                >
                                    <ChevronsRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style jsx="true">{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                .refresh-button:hover {
                    background-color: #f9fafb !important;
                    border-color: #9ca3af !important;
                    transform: translateY(-1px) !important;
                }
                
                .export-button:hover {
                    background-color: #059669 !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2) !important;
                }
                
                .add-button:hover {
                    background-color: #2563eb !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2) !important;
                }
                
                .stat-card:hover {
                    transform: translateY(-2px) !important;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
                }
                
                .search-input:focus {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
                }
                
                .select-input:focus {
                    border-color: #3b82f6 !important;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
                }
                
                .filter-tag:hover {
                    transform: translateY(-1px) !important;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                }
                
                .filter-tag-close:hover {
                    opacity: 1 !important;
                }
                
                .sortable-header-cell:hover {
                    background-color: #f3f4f6 !important;
                }
                
                .table-row:hover {
                    background-color: #f9fafb !important;
                }
                
                .email-link:hover {
                    color: #2563eb !important;
                    text-decoration: underline !important;
                }
                
                .status-button:hover {
                    transform: scale(1.05) !important;
                }
                
                .action-button-view:hover {
                    background-color: #dbeafe !important;
                    color: #1d4ed8 !important;
                    border-color: #bfdbfe !important;
                }
                
                .action-button-edit:hover {
                    background-color: #dcfce7 !important;
                    color: #15803d !important;
                    border-color: #bbf7d0 !important;
                }
                
                .action-button-delete:hover {
                    background-color: #fee2e2 !important;
                    color: #dc2626 !important;
                    border-color: #fecaca !important;
                }
                
                .action-button-more:hover {
                    background-color: #f3f4f6 !important;
                    color: #374151 !important;
                    border-color: #e5e7eb !important;
                }
                
                .pagination-button:hover:not(:disabled) {
                    background-color: #f9fafb !important;
                    border-color: #9ca3af !important;
                }
                
                .pagination-number:hover:not(.active) {
                    background-color: #f3f4f6 !important;
                    border-color: #9ca3af !important;
                }
                
                @media (max-width: 768px) {
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr) !important;
                    }
                    
                    .filters-grid {
                        grid-template-columns: 1fr !important;
                    }
                    
                    .header-actions {
                        flex-direction: column !important;
                        width: 100% !important;
                        gap: 8px !important;
                    }
                    
                    .header-actions button, 
                    .header-actions a {
                        width: 100% !important;
                        justify-content: center !important;
                    }
                    
                    .table-header {
                        flex-direction: column !important;
                        gap: 12px !important;
                        align-items: flex-start !important;
                    }
                    
                    .pagination-container {
                        flex-direction: column !important;
                        gap: 12px !important;
                        align-items: flex-start !important;
                    }
                    
                    .pagination-controls {
                        flex-wrap: wrap !important;
                    }
                    
                    .action-buttons {
                        flex-wrap: wrap !important;
                        gap: 4px !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .stats-grid {
                        grid-template-columns: 1fr !important;
                    }
                    
                    .table-wrapper {
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch !important;
                    }
                    
                    .table {
                        min-width: 800px !important;
                    }
                }
            `}</style>
        </div>
    );
};

// Loading skeleton styles
const skeletonContainer = {
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
};

const skeletonTitle = {
    height: '32px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    width: '200px',
    marginBottom: '24px'
};

const skeletonCard = {
    height: '96px',
    backgroundColor: '#e5e7eb',
    borderRadius: '8px'
};

const skeletonTable = {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    marginTop: '24px'
};

const skeletonHeader = {
    height: '48px',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    borderRadius: '8px 8px 0 0'
};

const skeletonRows = {
    padding: '16px'
};

const skeletonRow = {
    height: '64px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    marginBottom: '8px'
};

// Main Container Styles
const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    padding: '16px',
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
    animation: 'fadeIn 0.3s ease-in'
};

const innerContainerStyle = {
    maxWidth: '1200px',
    margin: '0 auto'
};

// Header Styles
const headerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    marginBottom: '24px',
    gap: '16px'
};

const titleStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '4px',
    margin: '0'
};

const subtitleStyle = {
    color: '#6b7280',
    margin: '0',
    fontSize: '14px'
};

const headerActionsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
};

const refreshButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    color: '#374151',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const refreshButtonDisabledStyle = {
    ...refreshButtonStyle,
    opacity: '0.5',
    cursor: 'not-allowed'
};

const exportButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#10b981',
    border: '1px solid #10b981',
    borderRadius: '6px',
    color: 'white',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const addButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    border: '1px solid #3b82f6',
    borderRadius: '6px',
    color: 'white',
    fontWeight: '500',
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    outline: 'none'
};

// Stats Cards Styles
const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px'
};

const statCardBaseStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
};

const statCardBlueStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)'
};

const statCardGreenStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
};

const statCardPurpleStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)'
};

const statCardAmberStyle = {
    ...statCardBaseStyle,
    background: 'linear-gradient(135deg, #fffbeb, #fef3c7)'
};

const statCardContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
};

const statLabelStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    marginBottom: '4px',
    margin: '0'
};

const statValueBaseStyle = {
    fontSize: '24px',
    fontWeight: '700',
    margin: '0'
};

const statValueBlueStyle = {
    ...statValueBaseStyle,
    color: '#1e40af'
};

const statValueGreenStyle = {
    ...statValueBaseStyle,
    color: '#065f46'
};

const statValuePurpleStyle = {
    ...statValueBaseStyle,
    color: '#7c3aed'
};

const statValueAmberStyle = {
    ...statValueBaseStyle,
    color: '#d97706'
};

const statIconBaseStyle = {
    padding: '12px',
    borderRadius: '8px'
};

const statIconBlueStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#dbeafe',
    color: '#1e40af'
};

const statIconGreenStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#d1fae5',
    color: '#065f46'
};

const statIconPurpleStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#f3e8ff',
    color: '#7c3aed'
};

const statIconAmberStyle = {
    ...statIconBaseStyle,
    backgroundColor: '#fef3c7',
    color: '#d97706'
};

// Filters Card Styles
const filtersCardStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    marginBottom: '24px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
};

const filtersCardHeaderStyle = {
    padding: '16px',
    borderBottom: '1px solid #e5e7eb'
};

const filtersTitleStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    margin: '0'
};

const filtersContentStyle = {
    padding: '16px'
};

const filtersGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px'
};

const filterLabelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px'
};

const searchContainerStyle = {
    position: 'relative'
};

const searchIconStyle = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    pointerEvents: 'none'
};

const searchInputStyle = {
    width: '100%',
    paddingLeft: '40px',
    paddingRight: '16px',
    paddingTop: '8px',
    paddingBottom: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const selectInputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
    color: '#374151',
    outline: 'none',
    cursor: 'pointer'
};

const activeFiltersStyle = {
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
};

const activeFiltersLabelStyle = {
    fontSize: '14px',
    color: '#6b7280'
};

const filterTagBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'all 0.2s ease'
};

const filterTagBlueStyle = {
    ...filterTagBaseStyle,
    backgroundColor: '#dbeafe',
    color: '#1e40af'
};

const filterTagGreenStyle = {
    ...filterTagBaseStyle,
    backgroundColor: '#d1fae5',
    color: '#065f46'
};

const filterTagPurpleStyle = {
    ...filterTagBaseStyle,
    backgroundColor: '#f3e8ff',
    color: '#7c3aed'
};

const filterTagCloseStyle = {
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '1',
    padding: '0',
    marginLeft: '4px',
    opacity: '0.7',
    transition: 'opacity 0.2s ease',
    outline: 'none'
};

// Table Card Styles
const tableCardStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
};

const tableHeaderStyle = {
    padding: '16px 24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px'
};

const tableTitleStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    margin: '0'
};

const tableSubtitleStyle = {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '4px',
    margin: '0'
};

const tableControlsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
};

const itemsPerPageStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const itemsPerPageLabelStyle = {
    fontSize: '14px',
    color: '#6b7280'
};

const itemsPerPageSelectStyle = {
    padding: '4px 8px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer'
};

const tableWrapperStyle = {
    overflowX: 'auto'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
};

const tableHeadStyle = {
    backgroundColor: '#f9fafb'
};

const headerCellStyle = {
    padding: '12px 24px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
};

const sortableHeaderCellStyle = {
    ...headerCellStyle,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.2s ease'
};

const headerCellContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
};

const sortIndicatorStyle = {
    color: '#9ca3af',
    fontSize: '10px'
};

const tableBodyStyle = {
    backgroundColor: 'white'
};

const tableRowStyle = {
    borderBottom: '1px solid #e5e7eb',
    transition: 'background-color 0.2s ease'
};

const tableCellStyle = {
    padding: '16px 24px',
    fontSize: '14px',
    color: '#374151',
    verticalAlign: 'middle'
};

// User Cell Styles
const userCellStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
};

const avatarContainerStyle = {
    position: 'relative'
};

const avatarStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #e5e7eb'
};

const avatarPlaceholderStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    border: '2px solid #e5e7eb'
};

const userNameStyle = {
    fontWeight: '600',
    color: '#111827',
    marginBottom: '2px'
};

const userIdStyle = {
    fontSize: '12px',
    color: '#6b7280'
};

const emailCellStyle = {
    display: 'flex',
    alignItems: 'center'
};

const emailLinkStyle = {
    color: '#3b82f6',
    textDecoration: 'none',
    transition: 'color 0.2s ease'
};

const roleBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    backgroundColor: '#f3e8ff',
    color: '#7c3aed',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '600'
};

const usernameStyle = {
    display: 'flex',
    alignItems: 'center'
};

const dateCellStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const dateValueStyle = {
    fontWeight: '500',
    color: '#111827'
};

const dateAgoStyle = {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '2px'
};

// Status Button Styles
const statusButtonBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    border: '1px solid'
};

const statusButtonActiveStyle = {
    ...statusButtonBaseStyle,
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderColor: '#10b981'
};

const statusButtonInactiveStyle = {
    ...statusButtonBaseStyle,
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderColor: '#ef4444'
};

// Action Buttons Styles
const actionButtonsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const actionButtonBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const actionButtonViewStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280',
    textDecoration: 'none'
};

const actionButtonEditStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280',
    textDecoration: 'none'
};

const actionButtonDeleteStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280'
};

const actionButtonMoreStyle = {
    ...actionButtonBaseStyle,
    color: '#6b7280'
};

// Empty State Styles
const emptyStateStyle = {
    padding: '48px 24px',
    textAlign: 'center'
};

const emptyStateContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
};

const emptyStateIconStyle = {
    color: '#9ca3af',
    marginBottom: '16px'
};

const emptyStateTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '0'
};

const emptyStateTextStyle = {
    color: '#6b7280',
    maxWidth: '400px',
    margin: '0 auto',
    lineHeight: '1.5'
};

// Pagination Styles
const paginationContainerStyle = {
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px'
};

const paginationInfoStyle = {
    fontSize: '14px',
    color: '#6b7280'
};

const paginationInfoBoldStyle = {
    fontWeight: '600',
    color: '#374151'
};

const paginationControlsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const paginationButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const paginationButtonDisabledStyle = {
    ...paginationButtonStyle,
    cursor: 'not-allowed',
    opacity: '0.5'
};

const paginationNumbersStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
};

const paginationNumberStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
};

const paginationNumberActiveStyle = {
    ...paginationNumberStyle,
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
};

const paginationEllipsisStyle = {
    padding: '0 8px',
    color: '#6b7280'
};

export default UsersListLayer;